import {
  FunctionComponent,
  memo,
  useState,
  SyntheticEvent,
  useEffect,
} from 'react';
import { Game as GameType } from '~types';
import Modal from './Modal';
import { useTranslation } from 'react-i18next';
import { useQuery, gql } from '@apollo/client';
import _ from 'lodash';
import { useYaMetrica } from '~frontend/provider/yametrica';
import { useModal } from './hooks';
import { GetName, SteamItemImage } from '../item';
import Currency from '../Currency';
import { Loader } from '../Loader';
import { Price } from '~ui/index';
import GameChange from '../game/GameChange';

interface OutItemModalData {
  getGame: GameType;
}

interface OutItemModalVars {
  id: number;
}

interface OutItemModalProps {
  id: number;
  onClose: any;
}

const OutItemModal: FunctionComponent<OutItemModalProps> = memo(
  ({ id = 0, onClose }: OutItemModalProps) => {
    const { reachGoal } = useYaMetrica();

    const { t } = useTranslation();
    const [type, setType] = useState(0);

    useEffect(() => {
      reachGoal('open_out_item_modal');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const { data, loading, error } = useQuery<
      OutItemModalData,
      OutItemModalVars
    >(
      gql`
        query OutItemModal($id: Int!) {
          getGame(id: $id) {
            id
            price
            priceSell
            status
            getItem {
              id
              color
              getImage
              getName
            }
          }
        }
      `,
      { variables: { id } },
    );

    const onSetType = (event: SyntheticEvent) => {
      if (event.type === 'mouseenter') {
        setType(_.toInteger(event.currentTarget.getAttribute('data-type')));
      } else {
        setType(0);
      }
    };

    const getGame = data?.getGame;

    if (loading) {
      return <Loader />;
    } else if (error) {
      return <>OutItemModal | Error! {error.message}</>;
    }

    return (
      <>
        <div className="withdraw-modal-item">
          <div
            className={`in-case-item status-${getGame?.status} ${
              getGame?.status === 1 && 'wait'
            }`}
            data-rar={getGame?.getItem?.color}
            data-audio="5.wav"
            data-volume="0.4"
          >
            <div className="in-case-cost">
              <Price sum={getGame?.price} />
              <Currency />
            </div>
            <div className="in-case-item-wrap">
              <div className="img">
                <SteamItemImage
                  url={getGame?.getItem?.getImage}
                  size="350x350"
                />
                <div className="honey"></div>
              </div>
              <div className="in-case-info">
                <div className="in-case-item-name">
                  <GetName getItem={getGame?.getItem} type={0} />
                </div>
                <div className="in-case-item-desc">
                  <GetName getItem={getGame?.getItem} type={1} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="withdraw-modal-content">
          <div className="title">{t('Choose withdraw method')}:</div>
          <div
            className="withdraw-modal-btns-item modal-btns-sell"
            data-type={0}
            onMouseEnter={onSetType}
            onMouseLeave={onSetType}
          >
            <GameChange
              className="btn btn-sell"
              getGame={getGame}
              type={0}
              onDone={onClose}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="42"
                height="42"
                viewBox="0 0 42 42"
              >
                <path
                  d="M1992,1385.667l16-16h-8V1367h13.333v13.333h-2.667v-8l-16,16Z"
                  transform="translate(-1981.333 -1356.333)"
                  fill="#fff"
                ></path>
              </svg>
              <span className="text">{t('Sell for')}</span>
              <div className="btn-sale-cost-wrap">
                <div className="btn-sale-cost">
                  <Price sum={getGame?.price} />
                  <Currency />
                </div>

                <div className="btn-sale-cost percent">+3%</div>
              </div>
              <div className="cost">
                <Price sum={getGame?.priceSell} />
                <Currency />
              </div>
            </GameChange>
            <div
              className="method-info"
              style={{ visibility: type === 0 ? 'unset' : 'hidden' }}
            >
              <div className="icon"></div>
              <div className="text">
                {t(
                  'You will instantly receive balance to your CS-Case account',
                )}{' '}
              </div>
            </div>
          </div>
          <div
            className="withdraw-modal-btns-item"
            data-type={1}
            onMouseEnter={onSetType}
            onMouseLeave={onSetType}
          >
            <GameChange
              className="btn btn-withdraw"
              getGame={getGame}
              type={1}
              onDone={onClose}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="42"
                height="42"
                viewBox="0 0 42 42"
              >
                <path
                  d="M1992,1385.667l16-16h-8V1367h13.333v13.333h-2.667v-8l-16,16Z"
                  transform="translate(-1981.333 -1356.333)"
                  fill="#fff"
                ></path>
              </svg>
              <span className="text">{t('Request trade')}</span>
            </GameChange>
            <div
              className="method-info"
              style={{ visibility: type === 1 ? 'unset' : 'hidden' }}
            >
              <div className="icon"></div>
              <div className="text">
                {t(
                  'In a couple of minutes you will receive a trade with the item on your Steam account',
                )}
                <span className="red">
                  {t('You need to take it within 5 minutes')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  },
);

const OutItemModalWrapper: FunctionComponent = () => {
  const modalName = 'OutItemModal';

  const {
    onClose,
    isOpen,
    props: { id },
  } = useModal<{ id: number }>(modalName);

  return (
    <Modal show={isOpen} onClose={onClose} width={800} modalName={modalName}>
      <div className="box-modal-wrap" id="withdraw-all-items">
        <div className="box-modal">
          <div
            className="box-modal_close arcticmodal-close"
            onClick={onClose}
          ></div>

          <div className="withdraw-modal">
            <OutItemModal id={id} onClose={onClose} />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default OutItemModalWrapper;
